import React, { useRef, useState } from "react";
import { Upload, X, FileText, Image as ImageIcon } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * FileUploader — wraps POST /api/upload and surfaces the resulting URL.
 * Props:
 *   value: string — current URL (controlled)
 *   onChange: (url: string) => void
 *   accept: "image/*" | "application/pdf,image/*" | etc
 *   label: button label
 *   testid: data-testid prefix
 */
export const FileUploader = ({ value, onChange, accept = "image/*,application/pdf", label = "Upload file", testid = "uploader" }) => {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);

    const handleChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("File must be under 10MB");
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(data.url || data.path || "");
            toast.success("Uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const isImage = value && /\.(png|jpg|jpeg|webp|gif)$/i.test(value);

    return (
        <div className="space-y-2" data-testid={`${testid}-wrap`}>
            <input ref={inputRef} type="file" accept={accept} onChange={handleChange} className="hidden" data-testid={`${testid}-input`} />
            {value ? (
                <div className="flex items-center gap-2 p-2 bg-[#F4F1EA] rounded-xl" data-testid={`${testid}-preview`}>
                    {isImage ? (
                        <a href={value} target="_blank" rel="noreferrer" className="flex items-center gap-2 min-w-0 flex-1">
                            <img src={value} alt="preview" className="w-10 h-10 object-cover rounded-md" />
                            <span className="text-xs text-[#4A5568] truncate">{value.split("/").pop()}</span>
                        </a>
                    ) : (
                        <a href={value} target="_blank" rel="noreferrer" className="flex items-center gap-2 min-w-0 flex-1">
                            <FileText className="w-5 h-5 text-[#4A5568]" />
                            <span className="text-xs text-[#4A5568] truncate">{value.split("/").pop() || value}</span>
                        </a>
                    )}
                    <button type="button" onClick={() => onChange("")} className="p-1 hover:bg-black/5 rounded" aria-label="Remove" data-testid={`${testid}-clear`}>
                        <X className="w-4 h-4 text-[#E53935]" />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={busy}
                    data-testid={`${testid}-btn`}
                    className="w-full inline-flex items-center justify-center gap-2 border border-dashed border-black/20 hover:border-[#E53935] hover:bg-[#F4F1EA] rounded-xl px-4 py-3 text-sm text-[#4A5568] hover:text-[#E53935] transition-colors disabled:opacity-50"
                >
                    {accept.includes("image") ? <ImageIcon className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                    {busy ? "Uploading…" : label}
                </button>
            )}
        </div>
    );
};

/**
 * MultiFileUploader — manages an array of URLs (for before/after photos).
 */
export const MultiFileUploader = ({ values = [], onChange, accept = "image/*", label = "Add photo", testid = "multi-uploader", max = 8 }) => {
    const add = (url) => {
        if (!url) return;
        if (values.length >= max) {
            toast.error(`Up to ${max} files`);
            return;
        }
        onChange([...values, url]);
    };
    const remove = (i) => onChange(values.filter((_, idx) => idx !== i));

    return (
        <div className="space-y-2" data-testid={`${testid}-wrap`}>
            {values.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {values.map((url, i) => (
                        <div key={i} className="relative group" data-testid={`${testid}-item-${i}`}>
                            <img src={url} alt={`item-${i}`} className="aspect-square w-full object-cover rounded-lg" />
                            <button type="button" onClick={() => remove(i)} className="absolute top-1 right-1 p-1 rounded-full bg-white/90 hover:bg-white shadow opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Remove">
                                <X className="w-3 h-3 text-[#E53935]" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {values.length < max && (
                <FileUploader value="" onChange={add} accept={accept} label={label} testid={`${testid}-add`} />
            )}
        </div>
    );
};

export default FileUploader;
